/**
 * Minimal HTML sanitiser for rendering user-supplied HTML in dangerouslySetInnerHTML.
 * Strips <script> blocks and inline event handlers (on*=) to prevent XSS.
 *
 * Used for HTML generated by this app's own PlateEditor (comments, one-pagers), which
 * legitimately relies on inline `style` (color/background/font/alignment are CONTENT,
 * not chrome — see plateToHtml.ts) and mention `data-*` attributes. Do NOT reach for this
 * to render HTML from an external/untrusted source — use sanitizeUntrustedHtml instead.
 *
 * NOTE: For production-grade sanitisation, replace this with DOMPurify if it is
 * added as a dependency. This implementation covers the primary injection vectors
 * without introducing a new package.
 *
 * Pattern reference: src/utils/utils.ts (utility function style).
 */

/**
 * Strips <script>...</script> blocks and inline event handler attributes (on*="...")
 * from an HTML string. Returns the sanitised string.
 */
export function sanitizeHtml(html: string): string {
    if (!html) return '';
    return (
        html
            // Remove <script ...>...</script> blocks (case-insensitive, across newlines)
            .replace(/<script\b[^>]*>[\s\S]*?<\/script>/gi, '')
            // Remove inline event handlers: onXxx="..." or onXxx='...'
            .replace(/\bon\w+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    );
}

/**
 * Stricter sanitiser for HTML from a genuinely untrusted, third-party source (e.g. an
 * inbound email body) — as opposed to sanitizeHtml, which covers this app's own editor
 * output. Parses into a detached DOM and walks it (not regex, so malformed/nested markup
 * can't slip past a pattern match): keeps only an allowlisted set of tags/attributes,
 * and strips <script>/<style>/<iframe>/<object>/<embed>/<form>, event handler attributes,
 * inline `style` (a CSS-based attack surface — e.g. position:fixed overlays), and
 * javascript:/vbscript: URLs.
 *
 * ponytail: DOMPurify would be the sturdier long-term choice, but adding it here forced a
 * hoisted-vs-nested version conflict against jspdf's existing (optional) dompurify@2.x dep
 * that isn't safe to hand-resolve in the lockfile in this environment. Revisit if DOMPurify
 * lands as a dependency for another reason.
 */

const ALLOWED_TAGS = new Set([
    'A',
    'B',
    'STRONG',
    'I',
    'EM',
    'U',
    'S',
    'STRIKE',
    'SUB',
    'SUP',
    'SMALL',
    'P',
    'DIV',
    'SPAN',
    'BR',
    'HR',
    'PRE',
    'CODE',
    'BLOCKQUOTE',
    'UL',
    'OL',
    'LI',
    'TABLE',
    'THEAD',
    'TBODY',
    'TR',
    'TD',
    'TH',
    'IMG',
    'H1',
    'H2',
    'H3',
    'H4',
    'H5',
    'H6',
]);

// Removed along with their entire subtree — unwrapping would leak raw script/CSS text.
const STRIP_ENTIRELY = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'FORM', 'NOSCRIPT', 'SVG', 'MATH', 'LINK', 'META', 'BASE']);

// `style` is handled specially (sanitizeStyle); the rest are benign presentational
// attributes an email body relies on (table borders/shading, cell alignment) that
// carry no script/URL surface.
// NOT `class`: <style> is stripped above, so a surviving class can only match the APP's CSS —
// global `.container{display:flex}` in ShimmerLoading.scss was flex-ing email bodies sideways.
const ALLOWED_ATTRS = new Set([
    'href',
    'src',
    'alt',
    'title',
    'colspan',
    'rowspan',
    'width',
    'height',
    'style',
    'align',
    'valign',
    'bgcolor',
    'border',
    'cellpadding',
    'cellspacing',
    // Durable mnemosyne docId the notification-engine stamps on a sent inline image —
    // cid: is unresolvable in a browser, so the viewer re-mints a presigned URL from
    // this at view time (see resolveCidImages.ts). Every other data-* stays stripped.
    'data-doc-id',
]);
const URL_ATTRS = new Set(['href', 'src']);
const UNSAFE_SCHEME = /^(javascript|vbscript):/i;
// Only safe raster image data: URIs pass; every other data: URI is blocked — notably
// data:image/svg+xml, which runs script when opened as a top-level document (e.g. by
// clicking an <a href>). data:text/html is likewise blocked.
const SAFE_DATA_URL = /^data:image\/(png|jpe?g|gif|webp)[;,]/i;

// Browsers strip ASCII tab/newline/CR (and other C0 controls) when resolving a URL's
// scheme, so `java\tscript:alert(1)` navigates as `javascript:`. Strip those chars before
// testing. The class is written as \x00-\x1f\x7f escape TEXT (never raw bytes) so this
// file stays reviewable text, not a binary blob. Entities are already decoded here.
function isUnsafeUrl(value: string): boolean {
    // eslint-disable-next-line no-control-regex
    const cleaned = value.replace(/[\x00-\x1f\x7f]/g, '').trim();
    if (UNSAFE_SCHEME.test(cleaned)) return true;
    if (/^data:/i.test(cleaned)) return !SAFE_DATA_URL.test(cleaned);
    return false;
}

// CSS properties that can escape the message box or track/exfiltrate — dropped from
// any inline style. Everything else (color, background, border, font, text-align,
// padding, margin, width, …) is CONTENT and kept so the modal renders the email as sent.
const UNSAFE_STYLE_PROP = /^(position|z-index|-.*)$/i;
// Values that can load a remote resource or run script — drop the whole declaration.
const UNSAFE_STYLE_VALUE = /url\s*\(|expression\s*\(|javascript:|vbscript:|@import|[<>]/i;

// Keeps benign inline CSS, drops overlay/tracking/script vectors declaration-by-declaration.
function sanitizeStyle(style: string): string {
    return style
        .split(';')
        .map((decl) => decl.trim())
        .filter(Boolean)
        .filter((decl) => {
            const idx = decl.indexOf(':');
            if (idx < 0) return false;
            const prop = decl.slice(0, idx).trim();
            const value = decl.slice(idx + 1);
            return !UNSAFE_STYLE_PROP.test(prop) && !UNSAFE_STYLE_VALUE.test(value);
        })
        .join('; ');
}

function sanitizeElement(el: Element): void {
    Array.from(el.children).forEach((child) => sanitizeElement(child));

    const tag = el.tagName;
    if (STRIP_ENTIRELY.has(tag)) {
        el.remove();
        return;
    }
    if (!ALLOWED_TAGS.has(tag)) {
        el.replaceWith(...Array.from(el.childNodes));
        return;
    }

    Array.from(el.attributes).forEach((attr) => {
        const name = attr.name.toLowerCase();
        if (name.startsWith('on') || !ALLOWED_ATTRS.has(name)) {
            el.removeAttribute(attr.name);
            return;
        }
        if (name === 'style') {
            const safe = sanitizeStyle(attr.value);
            if (safe) el.setAttribute('style', safe);
            else el.removeAttribute(attr.name);
            return;
        }
        if (URL_ATTRS.has(name) && isUnsafeUrl(attr.value)) {
            el.removeAttribute(attr.name);
        }
    });

    if (tag === 'A') {
        el.setAttribute('target', '_blank');
        el.setAttribute('rel', 'noopener noreferrer');
    }
}

/** Strips unsafe markup from a third-party HTML string (e.g. an email body). */
export function sanitizeUntrustedHtml(html: string): string {
    if (!html) return '';
    const doc = document.implementation.createHTMLDocument('');
    doc.body.innerHTML = html;
    Array.from(doc.body.children).forEach((child) => sanitizeElement(child));
    return doc.body.innerHTML;
}
